/**
 * Reads the product-lifecycle breakdown out of a Cisco Ready export.
 *
 * The SE can hand us several different things: the full Cisco Ready workbook in
 * any of Excel's formats, a smaller file someone already extracted, or text
 * pasted straight out of Excel. All of them end up here, and the caller does not
 * have to know which it was.
 *
 * In the full workbook the data lives on the "Summary - Product Lifecycle"
 * sheet, in the pivot table that starts at the "Row Labels" cell and ends at
 * "Grand Total". Between those two markers each row is either a category or a
 * sub-section of the one above it, and only those first two levels are wanted.
 *
 * Two readers, chosen by what the file actually is:
 *
 *  - `.xlsx` is a zip of XML, read directly with jszip. It is the common case
 *    and already a dependency, so it costs nothing extra.
 *  - `.xlsb` and legacy `.xls` are binary formats that need a real spreadsheet
 *    library. SheetJS is loaded lazily for those, so the bytes are only
 *    downloaded by the people who actually hand over a binary workbook.
 *
 * Not every export has the pivot: some accounts' Cisco Ready download omits
 * the "Summary - Product Lifecycle" sheet entirely, or ships it without the
 * "Row Labels"/"Grand Total" markers this reads by. Rather than reject the
 * whole upload, a zip (`.xlsx`/`.xlsb`) that has no readable pivot falls back
 * to rebuilding the same category/sub-category breakdown straight from the raw
 * asset sheet(s) — see `rawSheetsFallback`. Legacy `.xls` (an OLE compound
 * file, not a zip) has no such fallback: SheetJS would have to materialise the
 * whole workbook to scan for one, which is the exact cost this file exists to
 * avoid.
 *
 * Parsing runs in the browser rather than on the backend: the workbook can be
 * large, it is customer data, and this way the step works before the upload
 * endpoint exists.
 */

export interface LifecycleRow {
  category: string;
  /** Absent on a category row. */
  subCategory?: string;
  /** The row's total, where the sheet carried one. */
  value?: number;
  /** How many devices under this sub-category carry each exposure. */
  exposure?: Partial<Record<ExposureKind, number>>;
}

export interface LifecycleParse {
  rows: LifecycleRow[];
  /**
   * How the content was recognised, for telling the SE what we read.
   * `'raw'` means no pivot summary was found at all, and every row was rebuilt
   * from the raw asset sheet(s) instead — see `rawSheetsFallback`.
   */
  source: 'workbook' | 'extract' | 'text' | 'raw';
  /** Sheet the rows came from, when it was a workbook. */
  sheetName?: string;
  /**
   * How many categories were collapsed in the pivot and had to be rebuilt from
   * the raw asset sheet. Worth surfacing: those rows came from somewhere other
   * than the summary, and it explains why the upload took a moment.
   */
  expandedCategories?: number;
  /** How many raw asset sheets contributed, when `source` is `'raw'`. */
  rawSheetCount?: number;
  /**
   * Distinct products read off the raw asset sheet(s), deduplicated by PID. Present only when a
   * raw sheet was available (not for a pivot-only extract or pasted text); this is what the "import
   * to owned products" plan works from. The lifecycle summary above does not depend on it.
   */
  products?: InstallBaseProduct[];
}

export class LifecycleParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LifecycleParseError';
  }
}

import type { ExposureKind, InstallBaseCategory, InstallBaseLifecycle } from '../../api/types';
import type { InstallBaseFacts, InstallBaseProduct, ParseProgress } from './rawExpand';

const TARGET_SHEET = 'Summary - Product Lifecycle';
const START_MARKER = 'row labels';
const END_MARKER = 'grand total';

/**
 * The top level of Cisco Ready's product hierarchy.
 *
 * This exists because a pivot table copied out of Excel loses its shape: the
 * indent is a formatting property, not part of the cell text, so the clipboard
 * receives a completely flat list of labels. Without a way to tell a category
 * from a sub-section, every row would come through as a category.
 *
 * Anything not on this list is treated as a sub-section of the category above
 * it, so a new sub-section needs no change here. A genuinely new top-level
 * category would. See `looksLikeCategory` for the fallback that catches it.
 */
const KNOWN_CATEGORIES = [
  'Cisco Compute',
  'Collaboration',
  'Data Center Networking',
  'Enterprise Routing',
  'Enterprise Switching',
  'Internet Infrastructure',
  'Network Assurance',
  'Other',
  'Other Enterprise Networking',
  'Security',
  'Splunk',
  'Wireless',
];

const CATEGORY_SET = new Set(KNOWN_CATEGORIES.map((c) => c.toLowerCase()));

/**
 * Excel marks expandable rows with an outline glyph. A row that is expanded
 * (⊟) has children and is therefore a category; a collapsed one (⊞) is a leaf.
 * Present only on some clipboard paths, so it is a hint rather than the rule.
 */
const EXPANDED_GLYPHS = /^[\s]*[\u229f\u2296\u2212-]\s*/;
const COLLAPSED_GLYPHS = /^[\s]*[\u229e\u2295+]\s*/;

export function stripOutlineGlyph(label: string): string {
  return label.replace(/^[\s]*[\u229e\u229f\u2295\u2296+\u2212-]\s*/, '');
}

function looksLikeCategory(label: string): boolean {
  return CATEGORY_SET.has(stripOutlineGlyph(label).trim().toLowerCase());
}

/**
 * Depth for one row, using whichever signal the source actually carried:
 * Excel's indent attribute in a workbook, leading whitespace or an outline
 * glyph in a paste, and the known-category list when the paste came through
 * flat, which is the normal case.
 */
function depthOf(label: string, indent: number, sawIndent: boolean): number {
  if (indent > 0) return indent;
  const leading = label.length - label.trimStart().length;
  if (sawIndent && leading > 0) return Math.min(2, Math.ceil(leading / 2));
  if (sawIndent) return 0;

  // Nothing structural survived the clipboard. Fall back to the glyph, then to
  // the known top-level names.
  if (COLLAPSED_GLYPHS.test(label) && !looksLikeCategory(label)) return 1;
  if (EXPANDED_GLYPHS.test(label)) return 0;
  return looksLikeCategory(label) ? 0 : 1;
}

function toRows(entries: { label: string; indent: number; value?: number }[]): LifecycleRow[] {
  const rows: LifecycleRow[] = [];
  let current: string | null = null;

  // If any row carried real structure, trust it throughout and do not guess.
  const sawIndent = entries.some(
    (e) => e.indent > 0 || e.label.length !== e.label.trimStart().length,
  );

  for (const entry of entries) {
    const label = stripOutlineGlyph(entry.label).trim();
    if (!label) continue;
    const lower = label.toLowerCase();
    if (lower === START_MARKER || lower === END_MARKER) continue;

    const depth = depthOf(entry.label, entry.indent, sawIndent);
    if (depth === 0 || !current) {
      current = label;
      rows.push({ category: label, value: entry.value });
    } else if (depth === 1) {
      rows.push({ category: current, subCategory: label, value: entry.value });
    }
    // Deeper than a sub-section is deliberately dropped: only the first two
    // levels were asked for, and the deeper ones are product codes.
  }

  return rows;
}

// --- pasted text -----------------------------------------------------------

function parseNumber(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (!cleaned || !/^-?\d*\.?\d+$/.test(cleaned)) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Text copied out of Excel arrives tab-separated, one row per line, with the
 * hierarchy carried as leading whitespace on the label.
 */
export function parseLifecycleText(text: string): LifecycleParse {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const entries: { label: string; indent: number; value?: number }[] = [];

  // The header is the reliable anchor, but plenty of people select just the
  // rows. Start from the top when it is absent rather than refusing.
  const hasHeader = lines.some((l) => l.split('\t').some((c) => c.trim().toLowerCase() === START_MARKER));
  let started = !hasHeader;

  for (const line of lines) {
    if (!line.trim()) continue;
    const cells = line.split('\t');
    // The label is the first cell carrying text; a pasted pivot often has empty
    // leading columns because the table does not start at column A.
    const labelIndex = cells.findIndex((c) => c.trim().length > 0);
    if (labelIndex === -1) continue;
    const label = cells[labelIndex];
    const lower = stripOutlineGlyph(label).trim().toLowerCase();

    if (!started) {
      if (lower === START_MARKER) started = true;
      continue;
    }
    if (lower === START_MARKER) continue;
    if (lower === END_MARKER) break;

    // Rightmost numeric cell is the row total.
    let value: number | undefined;
    for (let i = cells.length - 1; i > labelIndex; i -= 1) {
      value = parseNumber(cells[i]);
      if (value !== undefined) break;
    }
    entries.push({ label, indent: 0, value });
  }

  if (entries.length === 0) {
    throw new LifecycleParseError(
      'Could not find any product rows in that text. Copy the Row Labels column from the Product Lifecycle sheet, from the first category down to "Grand Total".',
    );
  }
  return { rows: toRows(entries), source: 'text' };
}

// --- workbook --------------------------------------------------------------

/**
 * The XLSX parts are read by scanning rather than by building a DOM.
 *
 * Two reasons. A worker has no DOMParser, and this runs in one so a large
 * upload does not freeze the tab. And a full Cisco Ready export is hundreds of
 * megabytes: a DOM of it would cost several times the file size in memory,
 * where a scan costs almost nothing beyond the string itself.
 */

/** Reads one attribute out of a start tag such as `<c r="C16" t="s">`. */
function attrOf(tag: string, name: string): string | null {
  const match = new RegExp(`\\s${name}\\s*=\\s*"([^"]*)"`).exec(tag);
  return match ? match[1] : null;
}

/** Column letters from an A1-style reference. */
function columnOf(ref: string): string {
  return ref.replace(/[0-9]/g, '');
}

function rowOf(ref: string): number {
  return Number(ref.replace(/[^0-9]/g, '')) || 0;
}

type Zip = Awaited<ReturnType<typeof import('jszip')['loadAsync']>>;

/** Uncompressed size of a zip entry, when JSZip happens to know it. */
function entrySize(zip: Zip, path: string): number {
  const entry = zip.file(path) as unknown as { _data?: { uncompressedSize?: number } } | null;
  return entry?._data?.uncompressedSize ?? 0;
}

/** Anything larger than this is a data sheet, not a pivot summary. */
const EXTRACT_SCAN_LIMIT = 40 * 1024 * 1024;

/** Locates the target sheet and returns just that sheet's XML. */
async function locateSheet(zip: Zip): Promise<{ sheetXml: string; sheetName: string }> {
  const workbookXml = await zip.file('xl/workbook.xml')?.async('string');
  if (!workbookXml) {
    throw new LifecycleParseError('That file is not a readable Excel workbook.');
  }

  const sheetTags = workbookXml.match(/<sheet\b[^>]*\/?>/g) ?? [];
  const names = sheetTags.map((tag) => decodeXmlEntities(attrOf(tag, 'name') ?? ''));
  const relsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');

  const pathOf = (i: number): string => {
    const relId = attrOf(sheetTags[i], 'r:id') ?? attrOf(sheetTags[i], 'id');
    let target: string | null = null;
    if (relsXml && relId) {
      for (const tag of relsXml.match(/<Relationship\b[^>]*\/?>/g) ?? []) {
        if (attrOf(tag, 'Id') === relId) {
          target = attrOf(tag, 'Target');
          break;
        }
      }
    }
    // Fall back to positional naming when the relationship cannot be resolved.
    return target
      ? `xl/${target.replace(/^\/?xl\//, '').replace(/^\//, '')}`
      : `xl/worksheets/sheet${i + 1}.xml`;
  };

  // Exact name first, then a tolerant match: these exports have been seen with
  // different dashes and casing.
  const normalise = (s: string) => s.toLowerCase().replace(/[\s\u2010-\u2015-]+/g, ' ').trim();
  let index = names.findIndex((n) => n === TARGET_SHEET);
  if (index === -1) index = names.findIndex((n) => normalise(n) === normalise(TARGET_SHEET));

  if (index !== -1) {
    const sheetXml = await zip.file(pathOf(index))?.async('string');
    if (!sheetXml) throw new LifecycleParseError('That sheet could not be read from the workbook.');
    return { sheetXml, sheetName: names[index] };
  }

  // No sheet by that name, so this is an extract someone pulled out by hand and
  // saved under whatever name Excel gave it. Take the first sheet that actually
  // holds a pivot, skipping anything big enough to be a data sheet.
  const marker = await findExtractSheet(zip, names, pathOf);
  if (marker) return marker;

  throw new LifecycleParseError(
    `This workbook has no "${TARGET_SHEET}" sheet, and no other sheet in it had a "Row Labels" pivot. Sheets found: ${names.filter(Boolean).join(', ') || 'none'}.`,
  );
}

async function findExtractSheet(
  zip: Zip,
  names: string[],
  pathOf: (i: number) => string,
): Promise<{ sheetXml: string; sheetName: string } | null> {
  // "Row Labels" is normally a shared string, so the sheet XML holds an index
  // rather than the text. Resolve the indices that spell it first, then a sheet
  // referencing one of them is the pivot.
  const sharedXml = await zip.file('xl/sharedStrings.xml')?.async('string');
  const markerIndices = new Set<number>();
  if (sharedXml) {
    const siPattern = /<si\b[^>]*?(?:\/>|>([\s\S]*?)<\/si>)/g;
    let match: RegExpExecArray | null;
    let i = 0;
    while ((match = siPattern.exec(sharedXml)) !== null) {
      const body = match[1] ?? '';
      if (body && decodeXmlEntities(body.replace(/<[^>]*>/g, '')).trim().toLowerCase() === START_MARKER) {
        markerIndices.add(i);
      }
      i += 1;
    }
  }

  for (let i = 0; i < names.length; i += 1) {
    const path = pathOf(i);
    if (entrySize(zip, path) > EXTRACT_SCAN_LIMIT) continue;
    const sheetXml = await zip.file(path)?.async('string');
    if (!sheetXml) continue;

    const hasMarker =
      /<t\b[^>]*>\s*row labels\s*<\/t>/i.test(sheetXml) ||
      [...markerIndices].some((idx) =>
        new RegExp(`<c\\b[^>]*\\bt="s"[^>]*>\\s*<v>${idx}</v>`).test(sheetXml),
      );
    if (hasMarker) return { sheetXml, sheetName: names[i] };
  }
  return null;
}

/**
 * Shared strings for the indices the sheet actually referenced.
 *
 * A Cisco Ready export can run to hundreds of megabytes, and sharedStrings.xml
 * holds every unique string across every sheet in it, while the pivot summary
 * needs a few dozen of them. Decompressing that part into a single string is
 * the one step that would exhaust memory, so it is consumed as a stream: the
 * scan keeps only a small window, discards what it has passed, and stops as
 * soon as the highest referenced index has gone by.
 */
async function readSharedSubset(zip: Zip, needed: Set<number>): Promise<Map<number, string>> {
  const out = new Map<number, string>();
  if (needed.size === 0) return out;

  const entry = zip.file('xl/sharedStrings.xml');
  if (!entry) return out;

  const highest = Math.max(...needed);
  // Lazy, and with a self-closing branch: an empty "<si/>" that failed to match
  // would shift every index after it and silently mislabel the whole table.
  const siPattern = /<si\b[^>]*?(?:\/>|>([\s\S]*?)<\/si>)/g;
  const tPattern = /<t\b[^>]*?(?:\/>|>([\s\S]*?)<\/t>)/g;
  let index = 0;

  /** Pulls every complete `<si>` out of the window; returns what is left over. */
  const consume = (window: string): string => {
    siPattern.lastIndex = 0;
    let consumedTo = 0;
    let match: RegExpExecArray | null;
    while ((match = siPattern.exec(window)) !== null) {
      if (needed.has(index)) {
        // One string can be split across several runs; concatenate them.
        let text = '';
        let run: RegExpExecArray | null;
        tPattern.lastIndex = 0;
        while ((run = tPattern.exec(match[1] ?? '')) !== null) text += run[1] ?? '';
        out.set(index, decodeXmlEntities(text));
      }
      index += 1;
      consumedTo = siPattern.lastIndex;
      if (index > highest) break;
    }
    return window.slice(consumedTo);
  };

  // internalStream is part of JSZip but missing from its typings.
  const streamable = entry as unknown as {
    internalStream?: (type: 'string') => {
      on(event: 'data', cb: (chunk: string) => void): unknown;
      on(event: 'end', cb: () => void): unknown;
      on(event: 'error', cb: (err: Error) => void): unknown;
      resume(): unknown;
    };
  };

  if (typeof streamable.internalStream !== 'function') {
    // Older or patched JSZip: fall back to reading it whole.
    consume((await entry.async('string')) ?? '');
    return out;
  }

  await new Promise<void>((resolve, reject) => {
    let window = '';
    let finished = false;
    const stream = streamable.internalStream!('string');
    stream.on('data', (chunk: string) => {
      if (finished) return;
      window = consume(window + chunk);
      if (index > highest) {
        // Nothing further in the table is referenced; let the rest go.
        finished = true;
        window = '';
        resolve();
      }
    });
    stream.on('end', () => {
      if (!finished) {
        finished = true;
        resolve();
      }
    });
    stream.on('error', (err: Error) => {
      if (!finished) {
        finished = true;
        reject(err);
      }
    });
    stream.resume();
  });

  return out;
}

function decodeXmlEntities(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_m, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&amp;/g, '&');
}

/** Pivot hierarchy is expressed as the cell style's indent. */
async function readIndents(zip: Zip): Promise<number[]> {
  const stylesXml = await zip.file('xl/styles.xml')?.async('string');
  const indentByStyle: number[] = [];
  if (!stylesXml) return indentByStyle;

  // Only cellXfs matters here; cellStyleXfs earlier in the file uses the same
  // tag names, so the section is isolated before the formats are read.
  const section = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(stylesXml);
  if (!section) return indentByStyle;

  // The quantifier MUST be lazy. Excel writes most xf entries self-closing, and
  // a greedy [^>]* swallows the "/" of "<xf/>", so the self-closing branch never
  // matches and one match runs on to the next </xf>, silently merging entries
  // and shifting every style id after it.
  const xfPattern = /<xf\b[^>]*?(?:\/>|>([\s\S]*?)<\/xf>)/g;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = xfPattern.exec(section[1])) !== null) {
    const alignment = match[1] ? /<alignment\b[^>]*>/.exec(match[1]) : null;
    indentByStyle[i] = alignment ? Number(attrOf(alignment[0], 'indent') ?? 0) || 0 : 0;
    i += 1;
  }
  return indentByStyle;
}

interface SheetCell {
  ref: string;
  type: string | null;
  style: number;
  /** Raw `<v>` text, or the joined `<t>` runs for an inline string. */
  value: string;
}

/** Walks the sheet's cells once, without building a DOM. */
function scanCells(sheetXml: string): SheetCell[] {
  const cells: SheetCell[] = [];
  const cellPattern = /<c\b([^>]*?)(\/>|>([\s\S]*?)<\/c>)/g;
  let match: RegExpExecArray | null;

  while ((match = cellPattern.exec(sheetXml)) !== null) {
    const tag = match[1];
    const ref = attrOf(`<c${tag}>`, 'r');
    if (!ref) continue;

    const body = match[3] ?? '';
    const type = attrOf(`<c${tag}>`, 't');

    let value = '';
    if (type === 'inlineStr') {
      const tPattern = /<t\b[^>]*?(?:\/>|>([\s\S]*?)<\/t>)/g;
      let run: RegExpExecArray | null;
      while ((run = tPattern.exec(body)) !== null) value += run[1] ?? '';
    } else {
      value = /<v\b[^>]*?(?:\/>|>([\s\S]*?)<\/v>)/.exec(body)?.[1] ?? '';
    }

    cells.push({
      ref,
      type,
      style: Number(attrOf(`<c${tag}>`, 's') ?? -1),
      value,
    });
  }
  return cells;
}

export async function parseLifecycleWorkbook(zip: Zip): Promise<LifecycleParse> {
  const { sheetXml, sheetName } = await locateSheet(zip);
  const hasSheetData = sheetXml.includes('<sheetData');
  const allCells = scanCells(sheetXml);

  // Two passes over the sheet so the shared-string table can be read
  // selectively: first collect the indices this sheet references, then fetch
  // only those. The sheet is the small pivot summary, so walking it twice is
  // far cheaper than materialising every string in the workbook.
  const needed = new Set<number>();
  for (const cell of allCells) {
    if (cell.type !== 's') continue;
    const idx = Number(cell.value);
    if (Number.isInteger(idx) && idx >= 0) needed.add(idx);
  }

  const [shared, indentByStyle] = await Promise.all([
    readSharedSubset(zip, needed),
    readIndents(zip),
  ]);

  const cellText = (cell: SheetCell): string => {
    if (cell.type === 's') return shared.get(Number(cell.value)) ?? '';
    return decodeXmlEntities(cell.value);
  };

  // Find the "Row Labels" marker: normally C16, but locate it rather than
  // assume, because the export shifts when filters are added above it.
  let labelColumn: string | null = null;
  let startRow = 0;
  let endRow = Number.MAX_SAFE_INTEGER;

  for (const cell of allCells) {
    const text = cellText(cell).trim().toLowerCase();
    if (!labelColumn && text === START_MARKER) {
      labelColumn = columnOf(cell.ref);
      startRow = rowOf(cell.ref);
    } else if (labelColumn && columnOf(cell.ref) === labelColumn && text === END_MARKER) {
      endRow = rowOf(cell.ref);
      break;
    }
  }

  if (!labelColumn) {
    throw new LifecycleParseError(
      `Found the "${sheetName}" sheet but no "Row Labels" cell in it, so there was no pivot table to read.`,
    );
  }

  // Collect the label column, plus the rightmost numeric cell on each row.
  const byRow = new Map<number, { label?: string; indent: number; value?: number }>();
  for (const cell of allCells) {
    const row = rowOf(cell.ref);
    if (row <= startRow || row >= endRow) continue;

    const entry = byRow.get(row) ?? { indent: 0 };
    if (columnOf(cell.ref) === labelColumn) {
      entry.label = cellText(cell);
      entry.indent = cell.style >= 0 ? (indentByStyle[cell.style] ?? 0) : 0;
    } else if (cell.type !== 's') {
      const n = Number(cell.value);
      if (cell.value.trim() !== '' && Number.isFinite(n)) entry.value = n;
    }
    byRow.set(row, entry);
  }

  const entries = [...byRow.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, e]) => ({ label: e.label ?? '', indent: e.indent, value: e.value }))
    .filter((e) => e.label.trim().length > 0);

  const rows = toRows(entries);
  if (rows.length === 0) {
    throw new LifecycleParseError(
      `The "${sheetName}" sheet had no category rows between "Row Labels" and "Grand Total".`,
    );
  }

  // A workbook with one sheet is the extract someone pulled out by hand.
  const source: LifecycleParse['source'] = hasSheetData && rows.length > 0 ? 'workbook' : 'extract';
  return { rows, source, sheetName };
}

// --- binary workbooks (.xlsb, legacy .xls) ---------------------------------

/** Does this parsed sheet contain a "Row Labels" cell? */
function sheetHasMarker(sheet: import('xlsx').WorkSheet): boolean {
  for (const key of Object.keys(sheet)) {
    if (key.charCodeAt(0) === 33) continue; // "!" marks metadata, not a cell
    const cell = sheet[key] as { v?: unknown } | undefined;
    if (typeof cell?.v === 'string' && cell.v.trim().toLowerCase() === START_MARKER) return true;
  }
  return false;
}

/**
 * Both are binary formats, so the XML reader above cannot touch them. SheetJS
 * is imported lazily: it is a large library and most uploads never need it.
 *
 * Cell indent is not reliably exposed for these formats, so the hierarchy comes
 * from the known-category fallback in `depthOf`, the same path a flat paste
 * takes, which is already the normal case.
 */
async function parseBinaryWorkbook(
  file: ArrayBuffer,
  /** Whether searching every sheet for a pivot is safe on this file. */
  mayScanEverySheet: boolean,
): Promise<LifecycleParse> {
  const XLSX = await import('xlsx');
  let book;
  try {
    // `sheets` keeps SheetJS from materialising every sheet in the workbook.
    // On a full Cisco Ready export the other sheets carry the bulk of the data
    // and none of it is wanted, so parsing them would cost memory for nothing.
    book = XLSX.read(file, { type: 'array', sheetStubs: false, sheets: TARGET_SHEET });
  } catch {
    throw new LifecycleParseError('That workbook could not be read.');
  }

  const normalise = (s: string) => s.toLowerCase().replace(/[\s\u2010-\u2015-]+/g, ' ').trim();
  let sheetName: string | undefined = book.SheetNames.find((n) => n === TARGET_SHEET);

  if (!sheetName) {
    // The name did not match exactly, so nothing was parsed. Try the closest
    // match, then, for an extract saved under some arbitrary name, the first
    // sheet that actually holds a pivot. Each candidate is read on its own so
    // one oversized data sheet cannot pull the whole workbook into memory.
    //
    // Searching beyond the name match is only safe on a small file: the raw
    // asset sheet in a real export is hundreds of megabytes and SheetJS needs
    // several gigabytes to hold it. An extract is small by definition.
    const candidates = [
      ...book.SheetNames.filter((n) => normalise(n) === normalise(TARGET_SHEET)),
      ...(mayScanEverySheet
        ? book.SheetNames.filter((n) => normalise(n) !== normalise(TARGET_SHEET))
        : []),
    ];
    for (const candidate of candidates) {
      let attempt;
      try {
        attempt = XLSX.read(file, { type: 'array', sheetStubs: false, sheets: candidate });
      } catch {
        continue;
      }
      const ws = attempt.Sheets[candidate];
      if (ws && sheetHasMarker(ws)) {
        book = attempt;
        sheetName = candidate;
        break;
      }
    }
    if (!sheetName) {
      throw new LifecycleParseError(
        `This workbook has no "${TARGET_SHEET}" sheet, and no other sheet in it had a "Row Labels" pivot. Sheets found: ${book.SheetNames.join(', ') || 'none'}.`,
      );
    }
  }

  const sheet = book.Sheets[sheetName];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    blankrows: false,
    defval: null,
  });

  // Locate the pivot by its markers rather than assuming C16: the export shifts
  // whenever filters are added above the table.
  let labelCol = -1;
  let startRow = -1;
  for (let r = 0; r < grid.length && startRow === -1; r += 1) {
    const row = grid[r] ?? [];
    for (let c = 0; c < row.length; c += 1) {
      const cell = row[c];
      if (typeof cell === 'string' && cell.trim().toLowerCase() === START_MARKER) {
        labelCol = c;
        startRow = r;
        break;
      }
    }
  }
  if (labelCol === -1) {
    throw new LifecycleParseError(
      `Found the "${sheetName}" sheet but no "Row Labels" cell in it, so there was no pivot table to read.`,
    );
  }

  const entries: { label: string; indent: number; value?: number }[] = [];
  for (let r = startRow + 1; r < grid.length; r += 1) {
    const row = grid[r] ?? [];
    const raw = row[labelCol];
    if (typeof raw !== 'string' || !raw.trim()) continue;
    if (stripOutlineGlyph(raw).trim().toLowerCase() === END_MARKER) break;

    let value: number | undefined;
    for (let c = row.length - 1; c > labelCol; c -= 1) {
      const cell = row[c];
      if (typeof cell === 'number' && Number.isFinite(cell)) {
        value = cell;
        break;
      }
    }
    entries.push({ label: raw, indent: 0, value });
  }

  const rows = toRows(entries);
  if (rows.length === 0) {
    throw new LifecycleParseError(
      `The "${sheetName}" sheet had no category rows between "Row Labels" and "Grand Total".`,
    );
  }
  return { rows, source: 'workbook', sheetName };
}

// --- reading the raw asset sheet -------------------------------------------

/**
 * Adds everything the raw asset sheet knows: per-row asset counts, the install base
 * total, and where it stands against its own support dates. It also fills in
 * the sub-categories of any category the pivot was saved collapsed.
 *
 * Excel writes only the rows the pivot was displaying, so a collapsed category
 * contributes one row and nothing beneath it. Excel itself can still expand it
 * because it recomputes from the pivot cache, which on a real export is close
 * to a gigabyte, but the raw asset sheet behind that cache holds the same
 * category/sub-category pairing and can be streamed, so the expansion is done
 * here instead of sending the SE back to Excel to do it by hand.
 */
async function addInstallBaseFacts(
  zip: Zip,
  parse: LifecycleParse,
  onProgress?: ParseProgress,
): Promise<LifecycleParse> {
  const sheetPaths = Object.keys(zip.files).filter((p) =>
    /^xl\/worksheets\/sheet\d+\.(bin|xml)$/.test(p),
  );
  if (sheetPaths.length === 0) return parse;

  let facts: InstallBaseFacts | null = null;
  try {
    const { readInstallBaseFacts } = await import('./rawExpand');
    facts = await readInstallBaseFacts(zip, sheetPaths, onProgress);
  } catch {
    // An extract has no raw sheet, and a malformed one is not worth failing the
    // whole upload over. The summary we already read still stands.
    return parse;
  }
  if (!facts) return parse;

  const byName = new Map(
    [...facts.bySub].map(([label, subs]) => [label.trim().toLowerCase(), subs] as const),
  );

  const withSubs = new Set(
    parse.rows.filter((r) => r.subCategory).map((r) => r.category.trim().toLowerCase()),
  );

  const rows: LifecycleRow[] = [];
  let expandedCategories = 0;
  for (const row of parse.rows) {
    const key = row.category.trim().toLowerCase();

    if (row.subCategory) {
      // Attach the exposure to a sub-category the pivot already listed.
      const exposure = byName.get(key)?.get(row.subCategory.trim());
      rows.push(exposure ? { ...row, exposure } : row);
      continue;
    }

    rows.push(row);
    if (withSubs.has(key)) continue;

    // Nothing under this one in the pivot, so it was saved collapsed.
    const subs = byName.get(key);
    if (!subs || subs.size === 0) continue;
    expandedCategories += 1;
    for (const [sub, exposure] of [...subs].sort((a, b) => a[0].localeCompare(b[0]))) {
      rows.push({ category: row.category, subCategory: sub, exposure });
    }
  }

  return {
    ...parse,
    rows,
    ...(expandedCategories > 0 ? { expandedCategories } : {}),
    ...(facts.products.size > 0 ? { products: [...facts.products.values()] } : {}),
  };
}

/**
 * Rebuilds the category/sub-category breakdown straight from the raw asset
 * sheet(s), for a workbook that has no readable pivot at all.
 *
 * This is the same read `addInstallBaseFacts` uses to fill in a collapsed
 * category's children, just standing on its own: without a pivot there is no
 * `value` for a category and no expand/collapse state to account for, only the
 * category/sub-category pairs the raw rows actually contain. Returns `null`
 * rather than an empty `LifecycleParse` when nothing usable was found, so the
 * caller can fall through to the pivot's own, more specific error.
 */
async function rawSheetsFallback(
  zip: Zip,
  onProgress?: ParseProgress,
): Promise<LifecycleParse | null> {
  const sheetPaths = Object.keys(zip.files).filter((p) =>
    /^xl\/worksheets\/sheet\d+\.(bin|xml)$/.test(p),
  );
  if (sheetPaths.length === 0) return null;

  let facts: InstallBaseFacts | null = null;
  try {
    const { readInstallBaseFacts } = await import('./rawExpand');
    facts = await readInstallBaseFacts(zip, sheetPaths, onProgress);
  } catch {
    return null;
  }
  if (!facts || facts.bySub.size === 0) return null;

  const rows: LifecycleRow[] = [];
  for (const [category, subs] of [...facts.bySub].sort((a, b) => a[0].localeCompare(b[0]))) {
    if (subs.size === 0) {
      rows.push({ category });
      continue;
    }
    for (const [sub, exposure] of [...subs].sort((a, b) => a[0].localeCompare(b[0]))) {
      rows.push({ category, subCategory: sub, exposure });
    }
  }
  if (rows.length === 0) return null;

  return {
    rows,
    source: 'raw',
    rawSheetCount: facts.sheetCount,
    ...(facts.products.size > 0 ? { products: [...facts.products.values()] } : {}),
  };
}

/**
 * Reads whatever the SE handed over. The content decides how it is parsed, not
 * the extension. Runs inside the worker.
 */
export async function parseInstallBaseBuffer(
  buffer: ArrayBuffer,
  onProgress?: ParseProgress,
): Promise<LifecycleParse> {
  const head = new Uint8Array(buffer.slice(0, 8));
  const isZip = head[0] === 0x50 && head[1] === 0x4b; // "PK", so xlsx and xlsb

  // Legacy .xls is an OLE compound file, not a zip.
  const isOle =
    head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0;
  if (isOle) return parseBinaryWorkbook(buffer, true);

  if (isZip) {
    // .xlsb is a zip too, but its parts are binary rather than XML. Open it once
    // here and reuse it: a full Cisco Ready workbook is not cheap to unzip.
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(buffer);
    try {
      const parsed = zip.file('xl/workbook.bin')
        ? await parseBinaryWorkbook(buffer, false)
        : await parseLifecycleWorkbook(zip);
      return addInstallBaseFacts(zip, parsed, onProgress);
    } catch (err) {
      // No pivot to read at all is not the same failure as a corrupt file: the
      // raw asset sheet(s) may still say everything the pivot would have, so
      // that is tried before giving up on the upload entirely.
      if (!(err instanceof LifecycleParseError)) throw err;
      const fallback = await rawSheetsFallback(zip, onProgress);
      if (fallback) return fallback;
      throw err;
    }
  }

  // Anything else is treated as text: .csv, .tsv, or a saved paste.
  const text = new TextDecoder().decode(buffer);
  const asText = parseLifecycleText(text.includes('\t') ? text : text.replace(/,/g, '\t'));
  return { ...asText, source: 'extract' };
}

/** Worst first, which is the order the UI shows them in. */
export const EXPOSURE_ORDER: ExposureKind[] = [
  'pastSupport',
  'noSecurityFixes',
  'endingSoon',
  'pastSale',
  'uncovered',
];

/** How each exposure reads, and the column it was read from. */
export const EXPOSURE_LABELS: Record<ExposureKind, { label: string; source: string }> = {
  pastSupport: {
    label: 'LDoS',
    source: 'Last Date of Support has passed',
  },
  noSecurityFixes: {
    label: 'no security fixes',
    source: 'End of Vulnerability/Security Support Date has passed',
  },
  endingSoon: {
    label: 'support ends within 12 months',
    source: 'Last Date of Support falls in the next year',
  },
  pastSale: {
    label: 'end of product sale date passed',
    source: 'End of Product Sale Date has passed',
  },
  uncovered: {
    label: 'no support contract',
    source: 'Coverage reads "Not covered"',
  },
};

/** Collapses parsed rows into the category tree the UI shows. */
export function summarise(rows: LifecycleRow[]): InstallBaseCategory[] {
  const byCategory = new Map<string, InstallBaseCategory>();
  for (const row of rows) {
    const existing = byCategory.get(row.category) ?? { label: row.category, count: 0 };
    if (row.subCategory) {
      existing.count += 1;
      existing.subCategories = [
        ...(existing.subCategories ?? []),
        { label: row.subCategory, exposure: row.exposure ?? {} },
      ];
    }
    byCategory.set(row.category, existing);
  }
  return [...byCategory.values()];
}

/** How many sub-categories carry each kind of exposure. */
export function summariseExposure(
  categories: InstallBaseCategory[],
): InstallBaseLifecycle | undefined {
  const counts = Object.fromEntries(EXPOSURE_ORDER.map((k) => [k, 0])) as Record<
    ExposureKind,
    number
  >;
  let total = 0;
  let anyDated = false;

  for (const category of categories) {
    for (const sub of category.subCategories ?? []) {
      total += 1;
      for (const kind of EXPOSURE_ORDER) {
        if ((sub.exposure[kind] ?? 0) > 0) {
          counts[kind] += 1;
          anyDated = true;
        }
      }
    }
  }

  // Without the raw sheet there are no dates to judge against, and reporting
  // zeroes would read as "nothing is exposed" rather than "we cannot tell".
  return anyDated ? { counts, totalSubCategories: total } : undefined;
}
